import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Spin, Tag } from "antd";
import Swal from "sweetalert2";
import BorrowerHeader from "../../../Header/BorrowerHeader";
import BorrowerSidebar from "../../../SideBar/BorrowerSidebar";
import { ENV } from "../../../../config";
import { getBorrowerLoanEmiCards, payBorrowerEmiViaGateway, getBorrowerEmiGatewayStatus } from "../../../HttpRequest/afterlogin";

const feeRows = [
  { key: "platFormFeeIncludingGst", label: "Platform Fee (incl. GST)" },
  { key: "totalPenalty", label: "Total Penalty" },
  { key: "perDayPenalty", label: "Per Day Penalty" },
  { key: "emiBounceCharges", label: "EMI Bounce Charges" },
  { key: "technicalCharges", label: "Technical Charges" },
  { key: "recoveryCharges", label: "Recovery Charges" },
  { key: "extraInterestDays", label: "Extra Interest Days", isDay: true },
];

const BorrowerDisbursementInterestAmount = () => {
  const { borrowerId: bId, loanId: lId, id: rId } = useParams();
  const borrowerId = Number(bId);
  const loanId = lId !== "null" ? Number(lId) : null;
  const id = Number(rId);

  const [schedule, setSchedule] = useState({ loading: true, errorMessage: "", data: null });
  const [payingEmi, setPayingEmi] = useState(null);

  const pendingOrdersKey = `oxyloans_pending_emi_orders_${borrowerId}`;

  const readPendingOrders = () => {
    try {
      const savedOrders = JSON.parse(localStorage.getItem(pendingOrdersKey) || "[]");
      return Array.isArray(savedOrders) ? savedOrders.filter((order) => order?.orderId) : [];
    } catch (error) {
      return [];
    }
  };

  const writePendingOrders = (orders) => {
    if (orders.length) localStorage.setItem(pendingOrdersKey, JSON.stringify(orders));
    else localStorage.removeItem(pendingOrdersKey);
  };

  const savePendingOrder = (orderId, emiCardId) => {
    const orders = readPendingOrders().filter((order) => order.orderId !== orderId);
    orders.push({ orderId, emiCardId });
    writePendingOrders(orders);
  };

  const fmt = (value) => {
    const n = Number(value);
    return Number.isNaN(n) ? "-" : `₹ ${n.toFixed(2)}`;
  };

  const fmtDate = (value) => {
    if (!value) return "-";
    const d = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  };

  const getDateValue = (value) => {
    if (!value) return Number.POSITIVE_INFINITY;
    const parsed = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T00:00:00`)
      : new Date(value);
    return Number.isNaN(parsed.getTime()) ? Number.POSITIVE_INFINITY : parsed.getTime();
  };

  const getErr = (r) => r?.response?.data?.errorMessage || r?.response?.data?.message || r?.data?.errorMessage || r?.data?.message || "We could not load the EMI schedule. Please try again.";

  const getGatewayStatus = (statusResponse) => String(
    statusResponse?.data?.order_status ||
    statusResponse?.data?.paymentStatus ||
    statusResponse?.data?.orderStatus ||
    statusResponse?.data?.payment_status ||
    statusResponse?.data?.status ||
    "UNKNOWN"
  ).toUpperCase();

  const isTerminalGatewayStatus = (status) => [
    "PAID",
    "SUCCESS",
    "FAILED",
    "CANCELLED",
    "CANCELED",
    "EXPIRED",
  ].includes(status);

  const reconcilePendingOrders = async () => {
    const pendingOrders = readPendingOrders();
    if (!pendingOrders.length) return false;

    const remainingOrders = [];
    let hasTerminalOrder = false;
    for (const pendingOrder of pendingOrders) {
      try {
        const statusResponse = await getBorrowerEmiGatewayStatus(pendingOrder.orderId);
        const status = getGatewayStatus(statusResponse);
        if (isTerminalGatewayStatus(status)) hasTerminalOrder = true;
        else remainingOrders.push(pendingOrder);
      } catch (error) {
        remainingOrders.push(pendingOrder);
      }
    }
    writePendingOrders(remainingOrders);
    return hasTerminalOrder;
  };

  useEffect(() => {
    if (Number.isNaN(borrowerId) || Number.isNaN(id)) {
      setSchedule({ loading: false, errorMessage: "Missing details. Please go back and try again.", data: null });
      return;
    }
    const load = async () => {
      try {
        const res = await getBorrowerLoanEmiCards(loanId || id);
        if (res?.status === 200) {
          const raw = Array.isArray(res.data) ? res.data[0] : res.data;
          setSchedule({ loading: false, errorMessage: "", data: raw || null });
        } else {
          setSchedule({ loading: false, errorMessage: getErr(res), data: null });
        }
      } catch (e) {
        setSchedule({ loading: false, errorMessage: getErr(e), data: null });
      }
    };
    const loadPage = async () => {
      await reconcilePendingOrders();
      await load();
    };
    loadPage();
  }, [borrowerId, loanId, id]);

  const refreshSchedule = async () => {
    const res = await getBorrowerLoanEmiCards(loanId || id);
    if (res?.status === 200) setSchedule((current) => ({ ...current, data: Array.isArray(res.data) ? res.data[0] : res.data }));
  };

  const handleRepayment = async (emi) => {
    if (!emi?.emiCardId || payingEmi) return;
    setPayingEmi(emi.emiCardId);
    try {
      const response = await payBorrowerEmiViaGateway(emi.emiCardId);
      const sessionId = response?.data?.paymentSessionId;
      const orderId = response?.data?.order_id || response?.data?.orderId;
      if (!sessionId || !orderId || typeof window.Cashfree !== "function") throw new Error("Unable to start Cashfree payment.");
      savePendingOrder(orderId, emi.emiCardId);
      const cashfree = window.Cashfree({ mode: ENV === "production" ? "production" : "sandbox" });
      try {
        await cashfree.checkout({ paymentSessionId: sessionId, redirectTarget: "_self" });
      } catch (checkoutError) {}
      const statusResponse = await getBorrowerEmiGatewayStatus(orderId);
      const status = getGatewayStatus(statusResponse);
      const successful = status === "PAID" || status === "SUCCESS";
      if (isTerminalGatewayStatus(status)) {
        const remainingOrders = readPendingOrders().filter((order) => order.orderId !== orderId);
        writePendingOrders(remainingOrders);
      }
      await Swal.fire(successful ? "Payment successful" : "Payment not completed", successful ? "Your EMI payment has been recorded." : "The payment was not successful. You can try again.", successful ? "success" : "warning");
      await refreshSchedule();
    } catch (error) {
      Swal.fire("Payment failed", getErr(error), "error");
    } finally {
      setPayingEmi(null);
    }
  };

  const renderContent = () => {
    if (schedule.loading)
      return (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <Spin size="large" />
          <p style={{ color: "#64748b", marginTop: 12 }}>Loading repayment schedule...</p>
        </div>
      );

    if (schedule.errorMessage) return <div className="alert alert-danger">{schedule.errorMessage}</div>;

    if (!schedule.data) return <p className="text-muted mb-0">No EMI schedule available for this loan.</p>;

    const data = schedule.data;
    const emis = Array.isArray(data.emis) ? data.emis : [];
    const sortedEmis = [...emis].sort((first, second) => {
      const dateDifference = getDateValue(first.paymentDate) - getDateValue(second.paymentDate);
      if (dateDifference !== 0) return dateDifference;
      return Number(first.emiNumber || 0) - Number(second.emiNumber || 0);
    });
    const emisByYear = sortedEmis.reduce((groups, emi) => {
      const dateValue = getDateValue(emi.paymentDate);
      const year = Number.isFinite(dateValue) ? new Date(dateValue).getFullYear() : "Other";
      if (!groups[year]) groups[year] = [];
      groups[year].push(emi);
      return groups;
    }, {});
    const scheduleYears = Object.keys(emisByYear).sort((first, second) => {
      if (first === "Other") return 1;
      if (second === "Other") return -1;
      return Number(first) - Number(second);
    });
    const statusColor = { PAID: "green", SUCCESS: "green", FAILED: "red", UNPAID: "orange", UPCOMING: "blue" };

    return (
      <>
        <div className="row g-3 mb-4">
          {[['Disbursed amount', fmt(data.disbursedAmount)], ['Total EMIs', data.totalEmis ?? emis.length], ['Paid', data.paidCount ?? 0], ['Unpaid', data.unpaidCount ?? 0]].map(([label, value]) => <div className="col-6 col-md-3" key={label}><div className="border rounded p-3 h-100"><small className="text-muted d-block">{label}</small><strong>{value}</strong></div></div>)}
        </div>
        {/* <div
          style={{
            borderBottom: "1px solid #e2e8f0",
            paddingBottom: 10,
            marginBottom: 14,
          }}
        >
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Borrower ID: <strong>{borrowerId}</strong> &nbsp;|&nbsp; Loan ID:{" "}
            <strong>{loanId}</strong>
          </div>
        </div> */}

        {/* Fee Breakdown */}
        {/* <div style={{ border: "1px solid #e2e8f0", marginBottom: 16 }}>
          <div
            style={{
              padding: "8px 14px",
              borderBottom: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
              Charges Breakdown
            </span>
            <br/>
            <p style={{   color: "#1e293b" }}>
              Review all applicable charges, including platform fees, penalties,
              and interest
            </p>
          </div>
          {feeRows.map(({ key, label, isDay }, idx) => {
            const raw = data[key];
            const value = isDay ? `${raw ?? 0} days` : fmt(raw);
            return (
              <div
                key={key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 14px",
                  borderBottom:
                    idx < feeRows.length - 1 ? "1px solid #f1f5f9" : "none",
                }}
              >
                <span style={{ fontSize: 13, color: "#334155" }}>{label}</span>
                <span
                  style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}
                >
                  {value}
                </span>
              </div>
            );
          })}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 14px",
              borderTop: "2px solid #e2e8f0",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
              Grand Total
            </span>
            <span style={{ fontWeight: 800, fontSize: 15, color: "#1e293b" }}>
              {fmt(data.grandTotal)}
            </span>
          </div>
        </div> */}

        {/* Lender Table */}
        {/* <div style={{ border: "1px solid #e2e8f0" }}>
          <div
            style={{
              padding: "8px 14px",
              borderBottom: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
              Lender-wise Interest Details
            </span>{" "}<br/>
            <p style={{  color: "#1e293b" }}>
              View interest calculations for each lender based on amount,
              duration, and rate.
            </p>
          </div>
          <Table
            columns={tableColumns}
            dataSource={tableData}
            bordered={false}
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: true }}
            locale={{ emptyText: "No lender breakup found." }}
            style={{ borderRadius: 0 }}
          />
        </div> */}

        <div style={{ marginTop: 24 }}>
          <div style={{ marginBottom: 18 }}>
            <h5 style={{ marginBottom: 4, color: "#172b4d", fontWeight: 700 }}>Repayment schedule</h5>
            <p style={{ color: "#64748b", margin: 0 }}>{data.message || "Track each EMI by due date and payment status."}</p>
          </div>
          {scheduleYears.map((year) => (
            <section key={year} style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <span style={{ color: "#172b4d", fontSize: 18, fontWeight: 700 }}>{year}</span>
                <span style={{ color: "#64748b", fontSize: 13 }}>{emisByYear[year].length} payment{emisByYear[year].length === 1 ? "" : "s"}</span>
                <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {emisByYear[year].map((emi) => {
                  const status = String(emi.paymentStatus || "UPCOMING").toUpperCase();
                  const isPayable = status === "FAILED";
                  return (
                    <article key={emi.emiCardId} style={{ border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", padding: "14px 16px" }}>
                      <div className="row align-items-center g-3">
                        <div className="col-12 col-md-2">
                          <small style={{ display: "block", color: "#64748b", marginBottom: 3 }}>EMI {emi.emiNumber}</small>
                          <strong style={{ color: "#172b4d" }}>{fmtDate(emi.paymentDate)}</strong>
                        </div>
                        <div className="col-6 col-md-2"><small style={{ display: "block", color: "#64748b" }}>Amount</small><strong>{fmt(emi.amountToPay)}</strong></div>
                        <div className="col-6 col-md-2"><small style={{ display: "block", color: "#64748b" }}>Principal</small><span>{fmt(emi.principalAmount)}</span></div>
                        <div className="col-6 col-md-2"><small style={{ display: "block", color: "#64748b" }}>Interest</small><span>{fmt(emi.interestAmount)}</span></div>
                        <div className="col-6 col-md-2"><small style={{ display: "block", color: "#64748b" }}>Penalty</small><span>{fmt(emi.penaltyAmount)}</span></div>
                        <div className="col-6 col-md-1"><Tag color={statusColor[status] || "default"}>{status}</Tag></div>
                        <div className="col-6 col-md-1 text-md-end">{isPayable && <button type="button" className="btn btn-sm btn-primary" onClick={() => handleRepayment(emi)} disabled={payingEmi === emi.emiCardId}>{payingEmi === emi.emiCardId ? "..." : "Repay"}</button>}</div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="main-wrapper">
      <BorrowerHeader />
      <BorrowerSidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">EMI Details</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/borrowerDashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/borrowerDisbursementAmount">Loan Details</Link>
                  </li>
                  <li className="breadcrumb-item active">EMI Details</li>
                </ul>
              </div>
              <p className="">View a detailed breakdown of interest, fees, and charges applied to your loan. </p>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  {renderContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BorrowerDisbursementInterestAmount;
